import React, { useState, useMemo } from 'react';
import { ChevronLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion'; 
import { useAppContext } from '../../core/App';
import { useTelemetry } from '../../core/hooks/useTelemetry';
import { 
  CURRICULUM_MAP,
  professionalismModuleContent, 
  aiLiteracyModuleContent, 
  negotiationModuleContent, 
  renterModuleContent,
  interviewModuleContent,
  networkingModuleContent,
  techStackModuleContent,
  eqModuleContent,
  leadershipModuleContent,
  timeMgmtModuleContent
} from '../../core/constants/modules';
import ModuleHub from './components/ModuleHub';
import LessonPage from './components/LessonPage';
import { COMPLIANCE_FRAMEWORKS } from './data/rewards';
import RewardModal from '../../components/shared/RewardModal';

/**
 * ProfessionalismModule
 * Acts as the Universal Controller for all curriculum tracks.
 * Dynamically loads content based on moduleId.
 */
export default function ProfessionalismModule({ moduleId = 'pro-module', onComplete, isReviewMode }) {
  const { 
    handleNavigate, 
    pro101Progress,
    updatePro101Progress,
    userProfile 
  } = useAppContext();

  const { transmitTelemetry } = useTelemetry();

  // 1. DYNAMIC CONTENT MAPPING
  // This ensures the module controller can handle ANY track, not just Pro 101.
  const moduleContentMap = {
    'pro-module': professionalismModuleContent,
    'ai-lit': aiLiteracyModuleContent,
    'renter-module': renterModuleContent,
    'interview': interviewModuleContent,
    'negotiation': negotiationModuleContent,
    'networking': networkingModuleContent,
    'tech-stack': techStackModuleContent,
    'eq': eqModuleContent,
    'leadership': leadershipModuleContent,
    'time-mgmt': timeMgmtModuleContent
  };

  const moduleData = CURRICULUM_MAP.find(m => m.id === moduleId);
  const currentModuleContent = moduleContentMap[moduleId] || [];

  const [currentView, setCurrentView] = useState('hub');
  const [currentPartId, setCurrentPartId] = useState(null);
  
  const [activeReward, setActiveReward] = useState(null);
  const [justFinishedModule, setJustFinishedModule] = useState(false);

  const totalParts = currentModuleContent.length;
  
  const progressPercent = useMemo(() => {
    // Filter progress based on lessons actually in this specific module
    const completedCount = currentModuleContent.filter(part => pro101Progress[part.id]).length;
    return totalParts > 0 ? (completedCount / totalParts) * 100 : 0;
  }, [pro101Progress, currentModuleContent, totalParts]);
  
  const handleSelectPart = (partId) => {
    setCurrentView('lesson');
    setCurrentPartId(partId);
  };
  
  const handleGameComplete = async (partId, result) => {
    // --- RICH DATA EXTRACTION ---
    const isRichPayload = typeof result === 'object' && result !== null;
    const linkedGameId = isRichPayload ? (result.gameId || result.moduleId || result.id) : result;
    const isFirstTimeCompletingPart = !pro101Progress[partId];

    // --- TELEMETRY TRANSMISSION (DUMB CLIENT) ---
    if (isFirstTimeCompletingPart) {
      await transmitTelemetry({
        moduleId: 'COMPLETE_PRO_PART',
        rawInteractions: [{ status: 'success', partId: partId }]
      });
    }

    if (isRichPayload) {
      // CRITICAL PATCH: Accepts rawInteractions array if provided by child (e.g., retries log)
      await transmitTelemetry({
        moduleId: linkedGameId,
        rawInteractions: result.rawInteractions || [
          {
            status: result.score >= 80 ? 'success' : 'failed',
            score: result.score,
            answers: result.answers || null, 
            reactionTime: result.reactionTime,
            attempts: result.iterationCount,
            confidenceDelta: result.confidenceDelta,
            detectionRate: result.detectionRate,
            shadowAiMastered: linkedGameId === 'game_shadow_ai_defender' ? true : false
          }
        ]
      });
    }
    
    // Update local progress state
    const newProgress = { 
      ...pro101Progress, 
      [partId]: true, 
      [linkedGameId]: true 
    };
    
    await updatePro101Progress(newProgress);

    const allPartsDone = currentModuleContent.every(part => newProgress[part.id]);
    const isModuleMastered = userProfile?.completedModules?.[moduleId]?.status === 'mastered';

    let moduleFinishedNow = false;
    if (allPartsDone && !isModuleMastered) {
      setJustFinishedModule(true);
      moduleFinishedNow = true;

      await transmitTelemetry({
        moduleId: moduleId,
        rawInteractions: [{ status: 'success', type: 'module_mastery_trigger' }]
      });
    }
    
    const reward = COMPLIANCE_FRAMEWORKS.find(r => r.linkedGameId === linkedGameId);
    
    if (reward) {
      setActiveReward(reward);
    } else {
      if (moduleFinishedNow && !isReviewMode) {
        // CRITICAL PATCH: Defensive execution validation
        if (typeof onComplete === 'function') onComplete(); 
      } else {
        setCurrentView('hub');
        setCurrentPartId(null);
      }
    }
  };

  const handleRewardClosed = () => {
    setActiveReward(null);
    if (justFinishedModule && !isReviewMode) {
      // CRITICAL PATCH: Defensive execution validation
      if (typeof onComplete === 'function') onComplete(); 
    } else {
      setCurrentView('hub');
      setCurrentPartId(null);
    }
  };

  const handleExitLesson = () => {
    setCurrentView('hub');
    setCurrentPartId(null);
  };

  const currentPart = currentModuleContent.find(p => p.id === currentPartId);

  return (
    <div className="max-w-4xl mx-auto animate-fade-in pb-20 pt-6 relative min-h-screen">
      <div className="flex items-center mb-6 px-4 sm:px-0">
        <button 
          onClick={() => handleNavigate('curriculum-hub')}
          className="p-2 rounded-full bg-white shadow-sm hover:bg-gray-50 transition mr-4"
        >
          <ChevronLeft className="w-6 h-6 text-gray-600" />
        </button>
        <div>
          {/* Dynamic Title & Icon based on Module ID */}
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center gap-3">
            {moduleData?.icon && <moduleData.icon className={moduleData.color} />}
            {moduleData?.title || "Module"}
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Track Category: {moduleData?.category || "General Skills"}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-2xl overflow-hidden min-h-[600px]">
        <div className="p-6 sm:p-8 h-full">
          {/* REFACTORED: Structural fallback prevents fatal render crash if currentPart is lost during async state transitions */}
          {currentView === 'hub' || !currentPart ? (
            <ModuleHub
              content={currentModuleContent} 
              progress={pro101Progress}
              progressPercent={progressPercent}
              onSelectPart={handleSelectPart}
              isReviewMode={isReviewMode}
            />
          ) : (
            <LessonPage
              key={currentPart.id}
              part={currentPart}
              onComplete={(payload) => handleGameComplete(currentPartId, payload)}
              onExit={handleExitLesson}
              isReviewMode={isReviewMode} 
            />
          )}
        </div>
      </div>

      <AnimatePresence>
        {activeReward && (
          <RewardModal 
            reward={activeReward} 
            onClose={handleRewardClosed} 
          />
        )}
      </AnimatePresence>
    </div>
  );
}